import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { TaxRateSelect } from '../components/TaxRateSelect.tsx';
import { standardRateNow } from '../lib/taxes.ts';
import { Link, useSearchParams } from 'react-router';
import { CustomerPicker, type CustomerCard } from '../components/Customers.tsx';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { dateTime, day, money, parseMoney } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import type { OrderView } from '../lib/types.ts';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { PayDialog, useRegister } from './Pay.tsx';
import { downloadCsv, Kpi, Table } from './Reports.tsx';

/**
 * Rentals at the desk: today's pick-ups and returns, every rental with its state, the schedule
 * of what is out and booked, the items with their prices, the results and the rules. A rental
 * is booked (a quote or a reservation), handed over with the payment and the deposit, maybe
 * kept longer, and taken back with what came on top.
 */

type State = 'quote' | 'reserved' | 'out' | 'returned' | 'cancelled';
type DepositKind = 'none' | 'cash' | 'document';
type Filter = 'active' | 'quote' | 'reserved' | 'out' | 'returned' | 'cancelled' | 'all';
type Tab = 'today' | 'rentals' | 'schedule' | 'items' | 'reports' | 'settings';

interface RentalRow {
  id: string;
  number: number;
  state: State;
  customer: string;
  customer_name: string;
  customer_phone: string | null;
  location: string | null;
  location_name: string | null;
  starts: string;
  ends: string;
  starts_local: string;
  ends_local: string;
  total: number;
  deposit_kind: DepositKind;
  deposit: number;
  deposit_note: string | null;
  deposit_back_at: string | null;
  extension_fee: number;
  late_fee: number;
  damage_fee: number;
  damage_note: string | null;
  note: string | null;
  units: number;
  items: string | null;
  overdue: boolean;
  late_pickup: boolean;
  reserved_at: string | null;
  picked_up_at: string | null;
  returned_at: string | null;
  cancelled_at: string | null;
  cancel_reason: string | null;
  created_at: string;
}
interface Detail extends RentalRow {
  lines: { id: string; item: string; name: string; category: string | null; qty: number; unit_price: number; manual: boolean; deposit: number; total: number }[];
  hours: number;
  late: { hours: number; fee: number };
  grace_minutes: number;
  picked_up_by_name: string | null;
  returned_by_name: string | null;
  bill: OrderView | null;
  extra: OrderView | null;
  terms: string | null;
}
interface Overview {
  out: number;
  overdue: number;
  reserved: number;
  quotes: number;
  deposits: number;
  month: { sales: number; bills: number };
  units: { total: number; out: number };
  pickups: RentalRow[];
  returns: RentalRow[];
  items: { id: string; name: string; category: string | null; units: number; out: number }[];
  today: string;
}
interface Item {
  id: string;
  name: string;
  category: string | null;
  units: number;
  price_hour: number | null;
  price_day: number | null;
  price_week: number | null;
  deposit: number;
  vat: number;
  min_hours: number;
  buffer_minutes: number;
  description: string | null;
  sequence: number;
  active: boolean;
  out: number;
  coming: number;
  rentals_30: number;
  value_30: number;
}
interface Setup {
  settings: { grace_minutes: number; extras_vat: number; terms: string | null };
  locations: { id: string; name: string }[];
  today: string;
  now_local: string;
}
interface Check {
  lines: { item: string; name: string; qty: number; free: number; unit_price: number | null; price: { hours: number; price: number; weeks: number; days: number; hour_units: number } | null; total: number; ok: boolean; problem: string | null }[];
  hours: number;
  total: number;
  deposit: number;
  ok: boolean;
  problem: string | null;
}
interface Schedule {
  from: string;
  days: string[];
  today: string;
  starts: string;
  ends: string;
  items: { id: string; name: string; category: string | null; units: number; free: number[] }[];
  bars: { order: string; number: number; state: State; customer_name: string; item: string; qty: number; starts: string; ends: string; overdue: boolean }[];
}
interface Report {
  from: string;
  to: string;
  rentals: number;
  rent: number;
  time: number;
  damage: number;
  late: number;
  avg_hours: number;
  items: { item: string; name: string; rentals: number; qty: number; value: number; use_pct: number }[];
  cancels: { reason: string | null; n: number }[];
}

const TABS: { key: Tab; icon: IconName; permission?: string }[] = [
  { key: 'today', icon: 'clock' },
  { key: 'rentals', icon: 'receipt' },
  { key: 'schedule', icon: 'calendar' },
  { key: 'items', icon: 'box' },
  { key: 'reports', icon: 'trendUp' },
  { key: 'settings', icon: 'settings', permission: 'rental.manage' },
];

const STATE: Record<State, { tone: Tone; icon: IconName }> = {
  quote: { tone: 'neutral', icon: 'edit' },
  reserved: { tone: 'info', icon: 'calendar' },
  out: { tone: 'primary', icon: 'key' },
  returned: { tone: 'ok', icon: 'check' },
  cancelled: { tone: 'neutral', icon: 'x' },
};
export const rentalName = (n: number) => `R-${n}`;
/** "2026-10-27T09:30" → "27 Oct 2026, 09:30". */
const when = (local: string) => `${day(local)}, ${local.slice(11, 16)}`;
/** A naive local time moved by some hours, still in the business's wall-clock time. */
const shift = (local: string, hours: number) => new Date(new Date(`${local}:00Z`).getTime() + hours * 3_600_000).toISOString().slice(0, 16);
const hoursOf = (a: string, b: string) => Math.max(0, Math.ceil((new Date(`${b}:00Z`).getTime() - new Date(`${a}:00Z`).getTime()) / 3_600_000 - 1e-9));
const lek = (minor: number | null) => (minor === null || minor === undefined ? '' : String(minor / 100));

function length(hours: number) {
  if (hours < 24) return tn('rent.n_hours', hours);
  const d = Math.floor(hours / 24);
  const h = hours % 24;
  return h ? t('rent.days_hours', { d: tn('rent.n_days', d), h: tn('rent.n_hours', h) }) : tn('rent.n_days', d);
}

function StateBadge({ o }: { o: Pick<RentalRow, 'state' | 'overdue' | 'late_pickup'> }) {
  if (o.overdue) return <StatusBadge tone="danger" icon="alert" label={t('rent.st_overdue')} pulse />;
  if (o.late_pickup) return <StatusBadge tone="warn" icon="clock" label={t('rent.st_late_pickup')} />;
  const s = STATE[o.state];
  return <StatusBadge tone={s.tone} icon={s.icon} label={t(`rent.st_${o.state}` as MessageKey)} />;
}

export function Rental() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const tabs = TABS.filter((x) => !x.permission || can(x.permission));
  const tab = (tabs.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'today') as Tab;
  const { data: o, reload } = useAction<Overview>('rental.overview');
  const [version, setVersion] = useState(0);
  const [form, setForm] = useState<null | { id?: string; item?: string; starts?: string }>(null);
  const open = params.get('rental');
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) (v === null ? p.delete(k) : p.set(k, v));
    setParams(p, { replace: true });
  };
  const changed = async () => {
    setVersion((v) => v + 1);
    await reload();
  };
  useEffect(() => {
    const id = setInterval(() => {
      if (!document.hidden) void reload();
    }, 60_000);
    return () => clearInterval(id);
  }, [reload]);
  const show = (id: string | null) => set({ rental: id });

  return (
    <Shell>
      <div className="page reports rent-page">
        <div className="page-head">
          <div>
            <h1>{t('rent.title')}</h1>
            <p className="muted">{t('rent.subtitle')}</p>
          </div>
          {can('rental.handle') ? (
            <div className="page-actions">
              <Button variant="primary" icon="plus" onClick={() => setForm({})}>
                {t('rent.new')}
              </Button>
            </div>
          ) : null}
        </div>
        {!o ? (
          <Skeleton h={110} r="var(--radius-lg)" />
        ) : (
          <section className="rep-kpis" aria-label={t('rent.at_a_glance')}>
            <Kpi label={t('rent.k_out')} value={String(o.out)} hint={t('rent.k_units_out', { out: o.units.out, total: o.units.total })} />
            <Kpi label={t('rent.k_overdue')} value={String(o.overdue)} hint={o.overdue ? t('rent.k_overdue_hint') : t('rent.k_none_overdue')} warn={o.overdue > 0} />
            <Kpi label={t('rent.k_booked')} value={String(o.reserved)} hint={tn('rent.k_quotes', o.quotes)} />
            <Kpi label={t('rent.k_month')} value={money(o.month.sales)} hint={o.deposits ? t('rent.k_deposits', { amount: money(o.deposits) }) : tn('rent.k_bills', o.month.bills)} />
          </section>
        )}

        <div className="segmented tabs rent-tabs" role="tablist" aria-label={t('rent.sections')}>
          {tabs.map((x) => (
            <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => set({ tab: x.key === 'today' ? null : x.key, f: null })}>
              <Icon name={x.icon} size={16} />
              {t(`rent.tab_${x.key}` as MessageKey)}
              {x.key === 'today' && o && o.overdue ? <span className="count">{o.overdue}</span> : null}
            </button>
          ))}
        </div>

        {!o ? null : tab === 'today' ? (
          <TodayTab o={o} onOpen={show} />
        ) : tab === 'rentals' ? (
          <RentalsTab key={version} params={params} set={set} onOpen={show} />
        ) : tab === 'schedule' ? (
          <ScheduleTab key={version} onOpen={show} onBook={can('rental.handle') ? (item, starts) => setForm({ item, starts }) : undefined} />
        ) : tab === 'items' ? (
          <ItemsTab />
        ) : tab === 'reports' ? (
          <ReportsTab today={o.today} />
        ) : (
          <SettingsTab />
        )}
      </div>
      {open ? <RentalDialog key={`${open}-${version}`} id={open} onClose={() => show(null)} onChange={changed} onEdit={(id) => setForm({ id })} /> : null}
      {form ? (
        <RentalForm
          id={form.id}
          item={form.item}
          starts={form.starts}
          onClose={() => setForm(null)}
          onSaved={async (id) => {
            setForm(null);
            await changed();
            show(id);
          }}
        />
      ) : null}
    </Shell>
  );
}

// ---- today ----------------------------------------------------------------------------

function RentalRowButton({ r, onOpen, at }: { r: RentalRow; onOpen: (id: string) => void; at: 'starts' | 'ends' }) {
  return (
    <li>
      <button className="shop-todo-row" onClick={() => onOpen(r.id)}>
        <span className={`shop-todo-icon tone-${r.overdue ? 'danger' : r.late_pickup ? 'warn' : STATE[r.state].tone}`}>
          <Icon name={at === 'starts' ? 'key' : 'undo'} size={18} />
        </span>
        <span className="shop-todo-main">
          <strong>
            {rentalName(r.number)} · {r.customer_name}
          </strong>
          <span className="muted">
            {r.items} · {at === 'starts' ? t('rent.pickup_at', { time: r.starts_local.slice(11, 16) }) : t('rent.back_at', { time: when(r.ends_local) })}
          </span>
        </span>
        <span className="shop-todo-end">
          <strong className="tabular">{money(r.total)}</strong>
          <StateBadge o={r} />
        </span>
      </button>
    </li>
  );
}

function TodayTab({ o, onOpen }: { o: Overview; onOpen: (id: string) => void }) {
  return (
    <div className="shop-overview">
      <div className="form-stack">
        <section className="panel">
          <div>
            <h2 className="rep-sub">{t('rent.returns_today')}</h2>
            <p className="muted small">{t('rent.returns_hint')}</p>
          </div>
          {o.returns.length ? (
            <ul className="shop-todo">
              {o.returns.map((r) => (
                <RentalRowButton key={r.id} r={r} onOpen={onOpen} at="ends" />
              ))}
            </ul>
          ) : (
            <EmptyState icon="checkCircle" title={t('rent.no_returns')} />
          )}
        </section>
        <section className="panel">
          <div>
            <h2 className="rep-sub">{t('rent.pickups_today')}</h2>
            <p className="muted small">{t('rent.pickups_hint')}</p>
          </div>
          {o.pickups.length ? (
            <ul className="shop-todo">
              {o.pickups.map((r) => (
                <RentalRowButton key={r.id} r={r} onOpen={onOpen} at="starts" />
              ))}
            </ul>
          ) : (
            <EmptyState icon="calendar" title={t('rent.no_pickups')} />
          )}
        </section>
      </div>
      <div className="shop-side">
        <section className="panel">
          <h2 className="rep-sub">{t('rent.in_use')}</h2>
          {o.items.length ? (
            <ul className="rent-use">
              {o.items.map((i) => (
                <li key={i.id}>
                  <span className="rent-use-name">
                    <strong>{i.name}</strong>
                    <span className="muted small">{t('rent.out_of', { out: i.out, total: i.units })}</span>
                  </span>
                  <span className="rent-meter" role="img" aria-label={t('rent.out_of', { out: i.out, total: i.units })}>
                    <span style={{ width: `${Math.min(100, (i.out / Math.max(1, i.units)) * 100)}%` }} className={i.out >= i.units ? 'full' : ''} />
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">{t('rent.no_items_yet')}</p>
          )}
        </section>
      </div>
    </div>
  );
}

// ---- rentals ----------------------------------------------------------------------------

const FILTERS: Filter[] = ['active', 'out', 'reserved', 'quote', 'returned', 'cancelled', 'all'];

function RentalsTab({ params, set, onOpen }: { params: URLSearchParams; set: (n: Record<string, string | null>) => void; onOpen: (id: string) => void }) {
  const filter = (FILTERS.includes(params.get('f') as Filter) ? params.get('f') : 'active') as Filter;
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);
  const { data } = useAction<RentalRow[]>('rental.orders', { filter, ...(q ? { search: q } : {}) });
  return (
    <section className="panel">
      <div className="toolbar">
        <div className="segmented subtle rent-filters" role="radiogroup" aria-label={t('rent.show')}>
          {FILTERS.map((f) => (
            <button key={f} role="radio" aria-checked={filter === f} className={filter === f ? 'active' : ''} onClick={() => set({ f: f === 'active' ? null : f })}>
              {t(`rent.f_${f}` as MessageKey)}
            </button>
          ))}
        </div>
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={search} placeholder={t('rent.search')} aria-label={t('rent.search')} onChange={(e) => setSearch(e.target.value)} />
        </label>
      </div>
      {!data ? (
        <Skeleton h={240} />
      ) : (
        <Table
          view="rental.rentals"
          head={[t('rent.c_rental'), t('rent.c_customer'), t('rent.c_items'), t('rent.c_when'), t('rent.c_total'), t('rent.c_state')]}
          phoneHide={[0, 2, 3]}
          left={[1, 2, 3]}
          empty={filter === 'active' ? t('rent.none_active') : t('rent.none_found')}
          rows={data.map((r) => ({
            key: r.id,
            onClick: () => onOpen(r.id),
            cells: [
              <strong key="n" className="tabular">
                {rentalName(r.number)}
              </strong>,
              <span className="vat-party" key="c">
                <span>{r.customer_name}</span>
                <span className="muted">
                  <span className="shop-phone-only">{rentalName(r.number)} · </span>
                  {r.customer_phone ?? ''}
                </span>
              </span>,
              <span key="i">{r.items}</span>,
              <span className="vat-party" key="w">
                <span>{when(r.starts_local)}</span>
                <span className="muted">→ {when(r.ends_local)}</span>
              </span>,
              <span className="tabular" key="t">
                {money(r.total)}
              </span>,
              <StateBadge key="s" o={r} />,
            ],
          }))}
        />
      )}
    </section>
  );
}

// ---- one rental -----------------------------------------------------------------------------

function RentalDialog({ id, onClose, onChange, onEdit }: { id: string; onClose: () => void; onChange: () => Promise<void>; onEdit: (id: string) => void }) {
  const { can } = useSession();
  const { busy, run } = useSave();
  const { data: o, reload } = useAction<Detail>('rental.order', { id });
  const [ask, setAsk] = useState<null | 'cancel' | 'pickup' | 'extend' | 'return' | 'print'>(null);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const handle = can('rental.handle');
  const done = async () => {
    setAsk(null);
    await reload();
    await onChange();
  };
  const act = (name: string, input: Record<string, unknown>, msg: string) =>
    void run(async () => {
      await action(name, { id, ...input });
      await done();
    }, msg);
  if (!o) {
    return (
      <Dialog title={t('rent.rental')} onClose={onClose} size="lg">
        <Skeleton h={320} />
      </Dialog>
    );
  }
  const timeline: { label: string; at: string | null }[] = [
    { label: t('rent.tl_booked'), at: o.reserved_at && o.reserved_at < o.created_at ? o.reserved_at : o.created_at },
    { label: t('rent.tl_reserved'), at: o.reserved_at },
    { label: o.picked_up_by_name ? t('rent.tl_picked_by', { name: o.picked_up_by_name }) : t('rent.tl_picked'), at: o.picked_up_at },
    { label: o.returned_by_name ? t('rent.tl_returned_by', { name: o.returned_by_name }) : t('rent.tl_returned'), at: o.returned_at },
  ];
  const editable = o.state === 'quote' || o.state === 'reserved';
  return (
    <>
      <Dialog
        title={`${rentalName(o.number)} · ${o.customer_name}`}
        onClose={onClose}
        size="lg"
        footer={
          <>
            <Button variant="ghost" icon="printer" onClick={() => setAsk('print')}>
              {o.state === 'quote' ? t('rent.print_quote') : t('rent.print_agreement')}
            </Button>
            {handle && editable ? (
              <>
                <Button variant="ghost" icon="x" onClick={() => setAsk('cancel')}>
                  {t('rent.cancel')}
                </Button>
                <Button icon="edit" onClick={() => onEdit(o.id)}>
                  {t('rent.edit')}
                </Button>
              </>
            ) : null}
            {handle && o.state === 'quote' ? (
              <Button variant="primary" icon="calendar" busy={busy} onClick={() => act('rental.reserve', {}, t('rent.reserved_msg', { n: o.number }))}>
                {t('rent.reserve')}
              </Button>
            ) : null}
            {handle && o.state === 'reserved' && can('pos.pay') ? (
              <Button variant="primary" icon="key" onClick={() => setAsk('pickup')}>
                {t('rent.hand_over')}
              </Button>
            ) : null}
            {handle && o.state === 'out' ? (
              <>
                <Button icon="clock" onClick={() => setAsk('extend')}>
                  {t('rent.extend')}
                </Button>
                {can('pos.pay') ? (
                  <Button variant="primary" icon="undo" onClick={() => setAsk('return')}>
                    {t('rent.take_back')}
                  </Button>
                ) : null}
              </>
            ) : null}
          </>
        }
      >
        <div className="form-stack">
          <div className="item-summary">
            <div>
              <span className="muted">{t('rent.c_total')}</span>
              <strong className="tabular">{money(o.total + o.extension_fee + o.late_fee + o.damage_fee)}</strong>
            </div>
            <div>
              <span className="muted">{t('rent.length')}</span>
              <strong>{length(o.hours)}</strong>
            </div>
            <div>
              <span className="muted">{t('rent.deposit')}</span>
              <strong>{o.deposit_kind === 'cash' ? money(o.deposit) : o.deposit_kind === 'document' ? (o.deposit_note ?? t('rent.dep_document')) : t('rent.dep_none')}</strong>
            </div>
            <StateBadge o={o} />
          </div>

          {o.overdue ? (
            <p className="callout callout-danger">
              <Icon name="alert" size={16} />
              <span>{o.late.fee ? t('rent.overdue_fee', { time: length(o.late.hours), amount: money(o.late.fee) }) : t('rent.overdue_grace', { n: o.grace_minutes })}</span>
            </p>
          ) : o.late_pickup ? (
            <p className="callout callout-warn">
              <Icon name="clock" size={16} />
              <span>{t('rent.late_pickup_note')}</span>
            </p>
          ) : o.state === 'quote' ? (
            <p className="callout callout-info">
              <Icon name="note" size={16} />
              <span>{t('rent.quote_note')}</span>
            </p>
          ) : null}
          {o.state === 'cancelled' ? (
            <p className="callout">
              <Icon name="x" size={16} />
              <span>{[t('rent.cancelled_note'), o.cancel_reason].filter(Boolean).join(' · ')}</span>
            </p>
          ) : null}

          <div className="shop-detail">
            <section>
              <h3 className="rep-sub">{t('rent.items')}</h3>
              <table className="shop-items">
                <tbody>
                  {o.lines.map((l) => (
                    <tr key={l.id}>
                      <td className="tabular">{l.qty} ×</td>
                      <td>
                        {l.name}
                        {l.manual ? <span className="muted small"> · {t('rent.price_typed')}</span> : null}
                      </td>
                      <td className="tabular muted">{money(l.unit_price)}</td>
                      <td className="tabular">{money(l.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3}>{t('rent.rent')}</td>
                    <td className="tabular">{money(o.total)}</td>
                  </tr>
                  {o.extension_fee ? (
                    <tr>
                      <td colSpan={3}>{t('rent.extension')}</td>
                      <td className="tabular">{money(o.extension_fee)}</td>
                    </tr>
                  ) : null}
                  {o.late_fee ? (
                    <tr>
                      <td colSpan={3}>{t('rent.late_fee')}</td>
                      <td className="tabular">{money(o.late_fee)}</td>
                    </tr>
                  ) : null}
                  {o.damage_fee ? (
                    <tr>
                      <td colSpan={3}>
                        {t('rent.damage_fee')}
                        {o.damage_note ? <span className="muted small"> · {o.damage_note}</span> : null}
                      </td>
                      <td className="tabular">{money(o.damage_fee)}</td>
                    </tr>
                  ) : null}
                  <tr className="grand">
                    <td colSpan={3}>{t('rent.c_total')}</td>
                    <td className="tabular">{money(o.total + o.extension_fee + o.late_fee + o.damage_fee)}</td>
                  </tr>
                </tfoot>
              </table>
              <dl className="appr-facts rent-times">
                <dt>{t('rent.pickup')}</dt>
                <dd>{when(o.starts_local)}</dd>
                <dt>{t('rent.return')}</dt>
                <dd>{when(o.ends_local)}</dd>
              </dl>
            </section>
            <section>
              <h3 className="rep-sub">{t('rent.customer')}</h3>
              <dl className="appr-facts">
                <dt>{t('rent.c_customer')}</dt>
                <dd>
                  <Link className="link" to={`/customers/${o.customer}`}>{o.customer_name}</Link>
                </dd>
                {o.customer_phone ? (
                  <>
                    <dt>{t('rent.phone')}</dt>
                    <dd>
                      <a className="link" href={`tel:${o.customer_phone}`}>{o.customer_phone}</a>
                    </dd>
                  </>
                ) : null}
                {o.location_name ? (
                  <>
                    <dt>{t('rent.place')}</dt>
                    <dd>{o.location_name}</dd>
                  </>
                ) : null}
                {o.note ? (
                  <>
                    <dt>{t('rent.note')}</dt>
                    <dd>“{o.note}”</dd>
                  </>
                ) : null}
                {o.bill ? (
                  <>
                    <dt>{t('rent.bill')}</dt>
                    <dd>
                      {t('rent.bill_no', { n: o.bill.order.number })}
                      {o.bill.order.receipt_no ? ` · ${t('rent.receipt_no', { n: o.bill.order.receipt_no })}` : ''}
                    </dd>
                  </>
                ) : null}
                {o.extra ? (
                  <>
                    <dt>{t('rent.extra_bill')}</dt>
                    <dd>
                      {t('rent.bill_no', { n: o.extra.order.number })}
                      {o.extra.order.receipt_no ? ` · ${t('rent.receipt_no', { n: o.extra.order.receipt_no })}` : ''}
                    </dd>
                  </>
                ) : null}
                {o.deposit_kind !== 'none' ? (
                  <>
                    <dt>{t('rent.deposit')}</dt>
                    <dd>{o.deposit_back_at ? t('rent.deposit_back_on', { at: dateTime(o.deposit_back_at) }) : o.state === 'out' ? t('rent.deposit_held') : t('rent.deposit_at_pickup')}</dd>
                  </>
                ) : null}
              </dl>
              <h3 className="rep-sub">{t('rent.history')}</h3>
              <ol className="shop-timeline">
                {timeline.map((x) => (
                  <li key={x.label} className={x.at ? 'done' : ''}>
                    <span className="shop-tl-dot" aria-hidden="true" />
                    <span>{x.label}</span>
                    <span className="muted tabular">{x.at ? dateTime(x.at) : '—'}</span>
                  </li>
                ))}
              </ol>
            </section>
          </div>
        </div>
      </Dialog>
      {ask === 'cancel' ? (
        <Dialog
          title={t('rent.cancel_title', { n: o.number })}
          onClose={() => setAsk(null)}
          size="sm"
          footer={
            <Button
              variant="danger"
              icon="x"
              busy={busy}
              onClick={() => {
                setTried(true);
                if (reason.trim().length >= 3) act('rental.cancel', { reason: reason.trim() }, t('rent.cancelled_msg'));
              }}
            >
              {t('rent.cancel')}
            </Button>
          }
        >
          <p className="muted">{t('rent.cancel_note')}</p>
          <Field label={t('rent.reason')} error={tried && reason.trim().length < 3 ? t('rent.reason_needed') : null}>
            <input className="input" value={reason} maxLength={200} placeholder={t('rent.reason_example')} onChange={(e) => setReason(e.target.value)} />
          </Field>
        </Dialog>
      ) : null}
      {ask === 'pickup' ? <PickupDialog o={o} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'extend' ? <ExtendDialog o={o} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'return' ? <ReturnDialog o={o} onClose={() => setAsk(null)} onDone={done} /> : null}
      {ask === 'print' ? <PrintDialog o={o} onClose={() => setAsk(null)} /> : null}
    </>
  );
}

function NoShift({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <Dialog title={title} onClose={onClose} size="sm">
      <EmptyState icon="register" title={t('rent.no_shift')} body={t('rent.no_shift_body')} action={<Link className="btn btn-primary btn-md" to="/shift">{t('rent.open_shift')}</Link>} />
    </Dialog>
  );
}

/** Hand-over: what to give, the deposit to take, then the payment on the till. */
function PickupDialog({ o, onClose, onDone }: { o: Detail; onClose: () => void; onDone: () => Promise<void> }) {
  const { fail } = useUi();
  const { registers, register } = useRegister(o.location);
  const [note, setNote] = useState(o.deposit_note ?? '');
  const [view, setView] = useState<OrderView | null>(null);
  const [busy, setBusy] = useState(false);
  const title = t('rent.hand_over_title', { n: o.number });
  if (registers && !register?.session) return <NoShift title={title} onClose={onClose} />;
  const next = async () => {
    setBusy(true);
    try {
      setView(await action<OrderView>('rental.bill_preview', { id: o.id, kind: 'pickup' }));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  if (view) {
    return (
      <PayDialog
        view={view}
        onClose={() => setView(null)}
        pay={async (input) => action<OrderView & { change: number }>('rental.pickup', { id: o.id, ...input, deposit_note: o.deposit_kind === 'document' ? note.trim() || null : null })}
        next={{ label: t('rent.handed_over'), icon: 'check' }}
        cardReader={false}
        onPaid={() => void onDone()}
      />
    );
  }
  const noteMissing = o.deposit_kind === 'document' && !note.trim();
  return (
    <Dialog
      title={title}
      onClose={onClose}
      footer={
        <Button variant="primary" icon="cash" busy={busy} disabled={noteMissing} onClick={() => void next()}>
          {t('rent.take_payment', { amount: money(o.total) })}
        </Button>
      }
    >
      <div className="form-stack">
        <ul className="rent-check">
          {o.lines.map((l) => (
            <li key={l.id}>
              <Icon name="key" size={16} />
              <span>
                <strong>{l.qty} ×</strong> {l.name}
              </span>
            </li>
          ))}
        </ul>
        <p className="muted">{t('rent.until', { time: when(o.ends_local) })}</p>
        {o.deposit_kind === 'cash' ? (
          <p className="callout callout-info">
            <Icon name="cash" size={16} />
            <span>{t('rent.take_cash_deposit', { amount: money(o.deposit) })}</span>
          </p>
        ) : o.deposit_kind === 'document' ? (
          <Field label={t('rent.document_kept')} hint={t('rent.document_hint')} error={noteMissing ? t('rent.document_needed') : null}>
            <input className="input" value={note} maxLength={120} onChange={(e) => setNote(e.target.value)} />
          </Field>
        ) : null}
      </div>
    </Dialog>
  );
}

function ExtendDialog({ o, onClose, onDone }: { o: Detail; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [ends, setEnds] = useState(shift(o.ends_local, 24));
  const save = () =>
    void run(async () => {
      await action('rental.extend', { id: o.id, ends });
      await onDone();
    }, t('rent.extended_msg'));
  return (
    <Dialog
      title={t('rent.extend_title', { n: o.number })}
      onClose={onClose}
      size="sm"
      footer={
        <Button variant="primary" icon="check" busy={busy} disabled={ends <= o.ends_local} onClick={save}>
          {t('rent.extend')}
        </Button>
      }
    >
      <div className="form-stack">
        <p className="muted">{t('rent.extend_note', { time: when(o.ends_local) })}</p>
        <Field label={t('rent.new_return')}>
          <input className="input" type="datetime-local" value={ends} min={o.ends_local} onChange={(e) => e.target.value && setEnds(e.target.value)} />
        </Field>
        <div className="rent-quick">
          {[2, 24, 72, 168].map((h) => (
            <Button key={h} size="sm" variant="ghost" onClick={() => setEnds(shift(o.ends_local, h))}>
              + {length(h)}
            </Button>
          ))}
        </div>
      </div>
    </Dialog>
  );
}

/** Take-back: extra time, damage, the deposit back; what is owed on top is paid on the till. */
function ReturnDialog({ o, onClose, onDone }: { o: Detail; onClose: () => void; onDone: () => Promise<void> }) {
  const { fail, toast } = useUi();
  const { registers, register } = useRegister(o.location);
  const [lateText, setLateText] = useState(lek(o.late.fee));
  const [damageText, setDamageText] = useState('');
  const [note, setNote] = useState('');
  const [view, setView] = useState<OrderView | null>(null);
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const title = t('rent.take_back_title', { n: o.number });
  if (registers && !register?.session) return <NoShift title={title} onClose={onClose} />;
  const late = lateText.trim() ? parseMoney(lateText) : 0;
  const damage = damageText.trim() ? parseMoney(damageText) : 0;
  const extra = o.extension_fee + (late ?? 0) + (damage ?? 0);
  const deposit = o.deposit_kind === 'cash' ? o.deposit : 0;
  const errors = {
    late: late === null ? t('rent.e_amount') : null,
    damage: damage === null ? t('rent.e_amount') : null,
    note: damage && !note.trim() ? t('rent.e_damage_note') : null,
  };
  const input = { id: o.id, late_fee: late ?? 0, damage_fee: damage ?? 0, damage_note: note.trim() || null };
  const go = async () => {
    setTried(true);
    if (Object.values(errors).some(Boolean) || !register?.session) return;
    setBusy(true);
    try {
      if (extra > 0) {
        setView(await action<OrderView>('rental.bill_preview', { id: o.id, kind: 'return', late_fee: input.late_fee, damage_fee: input.damage_fee }));
      } else {
        await action('rental.return', { ...input, session: register.session.id, payments: [] });
        toast(deposit ? t('rent.returned_cash_msg', { amount: money(deposit) }) : t('rent.returned_msg'));
        await onDone();
      }
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  if (view) {
    return (
      <PayDialog
        view={view}
        onClose={() => setView(null)}
        pay={async (p) => {
          const r = await action<{ extra: OrderView; change: number }>('rental.return', { ...input, session: p.session, payments: p.payments });
          if (deposit) toast(t('rent.give_deposit_back', { amount: money(deposit) }));
          return { ...r.extra, change: r.change };
        }}
        next={{ label: t('rent.returned_done'), icon: 'check' }}
        cardReader={false}
        onPaid={() => void onDone()}
      />
    );
  }
  const err = (e: string | null) => (tried ? e : null);
  return (
    <Dialog
      title={title}
      onClose={onClose}
      footer={
        <Button variant="primary" icon={extra > 0 ? 'cash' : 'check'} busy={busy} onClick={() => void go()}>
          {extra > 0 ? t('rent.take_extra', { amount: money(extra) }) : t('rent.take_back')}
        </Button>
      }
    >
      <div className="form-stack">
        <ul className="rent-check">
          {o.lines.map((l) => (
            <li key={l.id}>
              <Icon name="check" size={16} />
              <span>
                <strong>{l.qty} ×</strong> {l.name}
              </span>
            </li>
          ))}
        </ul>
        {o.overdue ? (
          <p className="callout callout-warn">
            <Icon name="clock" size={16} />
            <span>{o.late.fee ? t('rent.late_suggest', { time: length(o.late.hours), amount: money(o.late.fee) }) : t('rent.overdue_grace', { n: o.grace_minutes })}</span>
          </p>
        ) : null}
        <div className="form-grid">
          <Field label={t('rent.late_fee')} hint={t('rent.late_fee_hint')} error={err(errors.late)}>
            <input className="input" inputMode="decimal" value={lateText} placeholder="0" onChange={(e) => setLateText(e.target.value)} />
          </Field>
          <Field label={t('rent.damage_fee')} hint={t('rent.damage_fee_hint')} error={err(errors.damage)}>
            <input className="input" inputMode="decimal" value={damageText} placeholder="0" onChange={(e) => setDamageText(e.target.value)} />
          </Field>
        </div>
        {damage ? (
          <Field label={t('rent.damage_note')} error={err(errors.note)} required>
            <input className="input" value={note} maxLength={500} placeholder={t('rent.damage_example')} onChange={(e) => setNote(e.target.value)} />
          </Field>
        ) : null}
        <dl className="rent-sum">
          {o.extension_fee ? (
            <>
              <dt>{t('rent.extension')}</dt>
              <dd className="tabular">{money(o.extension_fee)}</dd>
            </>
          ) : null}
          <dt>{t('rent.owed_on_top')}</dt>
          <dd className="tabular">{money(extra)}</dd>
          {o.deposit_kind === 'cash' ? (
            <>
              <dt>{t('rent.deposit_back')}</dt>
              <dd className="tabular">− {money(deposit)}</dd>
            </>
          ) : o.deposit_kind === 'document' ? (
            <>
              <dt>{t('rent.give_back_document')}</dt>
              <dd>{o.deposit_note}</dd>
            </>
          ) : null}
        </dl>
        {o.deposit_kind === 'cash' && extra > 0 ? <p className="muted small">{t('rent.net_hint', { amount: money(Math.abs(deposit - extra)), dir: deposit >= extra ? t('rent.to_customer') : t('rent.from_customer') })}</p> : null}
      </div>
    </Dialog>
  );
}

/** A quote or a rental agreement on paper: the business prints it, the customer signs. */
function PrintDialog({ o, onClose }: { o: Detail; onClose: () => void }) {
  return (
    <Dialog
      title={o.state === 'quote' ? t('rent.print_quote') : t('rent.print_agreement')}
      onClose={onClose}
      size="lg"
      footer={
        <Button
          variant="primary"
          icon="printer"
          onClick={() => {
            document.body.classList.add('printing-rental');
            window.print();
            document.body.classList.remove('printing-rental');
          }}
        >
          {t('rent.print')}
        </Button>
      }
    >
      <article className="rent-paper">
        <header>
          <h2>{o.state === 'quote' ? t('rent.paper_quote', { n: rentalName(o.number) }) : t('rent.paper_agreement', { n: rentalName(o.number) })}</h2>
          <p className="muted">{dateTime(new Date().toISOString())}</p>
        </header>
        <dl className="appr-facts">
          <dt>{t('rent.c_customer')}</dt>
          <dd>
            {o.customer_name}
            {o.customer_phone ? ` · ${o.customer_phone}` : ''}
          </dd>
          <dt>{t('rent.pickup')}</dt>
          <dd>{when(o.starts_local)}</dd>
          <dt>{t('rent.return')}</dt>
          <dd>{when(o.ends_local)}</dd>
          <dt>{t('rent.deposit')}</dt>
          <dd>{o.deposit_kind === 'cash' ? money(o.deposit) : o.deposit_kind === 'document' ? (o.deposit_note ?? t('rent.dep_document')) : t('rent.dep_none')}</dd>
        </dl>
        <table className="shop-items">
          <tbody>
            {o.lines.map((l) => (
              <tr key={l.id}>
                <td className="tabular">{l.qty} ×</td>
                <td>{l.name}</td>
                <td className="tabular">{money(l.total)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="grand">
              <td colSpan={2}>{t('rent.c_total')}</td>
              <td className="tabular">{money(o.total)}</td>
            </tr>
          </tfoot>
        </table>
        {o.terms ? <p className="rent-terms">{o.terms}</p> : null}
        {o.state !== 'quote' ? (
          <div className="rent-signs">
            <span>{t('rent.sign_customer')}</span>
            <span>{t('rent.sign_staff')}</span>
          </div>
        ) : null}
      </article>
    </Dialog>
  );
}

// ---- new or changed rental -----------------------------------------------------------------

interface FormLine {
  key: number;
  item: string;
  qty: number;
  price: string;
}

function RentalForm({ id, item, starts: startsAt, onClose, onSaved }: { id?: string; item?: string; starts?: string; onClose: () => void; onSaved: (id: string) => Promise<void> }) {
  const { fail } = useUi();
  const { busy, run } = useSave();
  const { data: setup } = useAction<Setup>('rental.setup');
  const { data: items } = useAction<Item[]>('rental.items');
  const [old, setOld] = useState<Detail | null>(null);
  const [customer, setCustomer] = useState<{ id: string; name: string; phone: string | null } | null>(null);
  const [picking, setPicking] = useState(false);
  const [starts, setStarts] = useState('');
  const [ends, setEnds] = useState('');
  const [lines, setLines] = useState<FormLine[]>([]);
  const [depositKind, setDepositKind] = useState<DepositKind>('cash');
  const [depositText, setDepositText] = useState('');
  const [depositTouched, setDepositTouched] = useState(false);
  const [depositNote, setDepositNote] = useState('');
  const [note, setNote] = useState('');
  const [location, setLocation] = useState<string | null>(null);
  const [check, setCheck] = useState<Check | null>(null);
  const [tried, setTried] = useState(false);
  const [counter, setCounter] = useState(1);

  // A new rental starts at the next half hour; a changed one loads what it is.
  useEffect(() => {
    if (!setup || starts) return;
    if (id) {
      void action<Detail>('rental.order', { id }).then((d) => {
        setOld(d);
        setCustomer({ id: d.customer, name: d.customer_name, phone: d.customer_phone });
        setStarts(d.starts_local);
        setEnds(d.ends_local);
        setLines(d.lines.map((l, i) => ({ key: i, item: l.item, qty: l.qty, price: l.manual ? lek(l.unit_price) : '' })));
        setCounter(d.lines.length + 1);
        setDepositKind(d.deposit_kind);
        setDepositText(lek(d.deposit));
        setDepositTouched(true);
        setDepositNote(d.deposit_note ?? '');
        setNote(d.note ?? '');
        setLocation(d.location);
      }, fail);
      return;
    }
    const now = setup.now_local;
    const m = Number(now.slice(14, 16));
    const start = startsAt && startsAt > now ? `${startsAt}T09:00` : shift(`${now.slice(0, 14)}00`, m < 30 ? 0.5 : 1);
    setStarts(start);
    setEnds(shift(start, 3));
    setLines(item ? [{ key: 0, item, qty: 1, price: '' }] : []);
  }, [setup, id]);
  useEffect(() => {
    if (!items || id || lines.length || item) return;
    const first = items.find((i) => i.active);
    if (first) setLines([{ key: 0, item: first.id, qty: 1, price: '' }]);
  }, [items]);

  // Prices and free units while the form is filled in.
  const wanted = lines.filter((l) => l.item);
  const key = JSON.stringify([starts, ends, wanted.map((l) => [l.item, l.qty, l.price])]);
  useEffect(() => {
    if (!starts || !ends || !wanted.length) {
      setCheck(null);
      return;
    }
    const h = setTimeout(() => {
      action<Check>('rental.check', {
        starts,
        ends,
        order: id ?? null,
        lines: wanted.map((l) => ({ item: l.item, qty: l.qty, unit_price: l.price.trim() ? (parseMoney(l.price) ?? null) : null })),
      }).then(setCheck, () => setCheck(null));
    }, 250);
    return () => clearTimeout(h);
  }, [key]);
  // The deposit follows the items until it is typed.
  useEffect(() => {
    if (!depositTouched && check) setDepositText(lek(check.deposit) || '');
  }, [check?.deposit]);

  const active = (items ?? []).filter((i) => i.active || lines.some((l) => l.item === i.id));
  const deposit = depositText.trim() ? parseMoney(depositText) : 0;
  const errors = {
    customer: !customer ? t('rent.e_customer') : null,
    times: starts && ends && ends <= starts ? t('rent.e_times') : null,
    lines: !wanted.length ? t('rent.e_lines') : null,
    deposit: depositKind === 'cash' && (deposit === null || !deposit) ? t('rent.e_deposit') : null,
    note: depositKind === 'document' && !depositNote.trim() ? t('rent.e_document') : null,
  };
  const save = (reserve: boolean) => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(
      async () => {
        const r = await action<RentalRow>('rental.save', {
          id: id ?? null,
          customer: customer!.id,
          location,
          starts,
          ends,
          lines: wanted.map((l) => ({ item: l.item, qty: l.qty, unit_price: l.price.trim() ? parseMoney(l.price) : null })),
          deposit_kind: depositKind,
          deposit: depositKind === 'cash' ? (deposit ?? 0) : 0,
          deposit_note: depositKind === 'document' ? depositNote.trim() : null,
          note: note.trim() || null,
          reserve,
        });
        await onSaved(r.id);
      },
      reserve ? t('rent.saved_reserved') : t('rent.saved_quote'),
    );
  };
  const err = (e: string | null) => (tried ? e : null);
  const hours = starts && ends ? hoursOf(starts, ends) : 0;
  const setLine = (k: number, patch: Partial<FormLine>) => setLines((ls) => ls.map((l) => (l.key === k ? { ...l, ...patch } : l)));
  const reserved = old?.state === 'reserved';

  if (picking) {
    return (
      <CustomerPicker
        title={t('rent.choose_customer')}
        onClose={() => setPicking(false)}
        onPick={(c: CustomerCard) => {
          setCustomer({ id: c.id, name: c.name, phone: c.phone });
          setPicking(false);
        }}
      />
    );
  }
  return (
    <Dialog
      title={id ? t('rent.edit_title', { n: old?.number ?? '' }) : t('rent.new')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          {!reserved ? (
            <Button variant="secondary" icon="edit" busy={busy} onClick={() => save(false)}>
              {t('rent.save_quote')}
            </Button>
          ) : null}
          <Button variant="primary" icon="calendar" busy={busy} disabled={!!check && !check.ok} onClick={() => save(true)}>
            {reserved ? t('common.save') : t('rent.reserve_total', { amount: money(check?.total ?? 0) })}
          </Button>
        </>
      }
    >
      {!setup || !items || !starts ? (
        <Skeleton h={360} />
      ) : !items.length ? (
        <EmptyState icon="box" title={t('rent.no_items_yet')} body={t('rent.no_items_hint')} />
      ) : (
        <div className="rent-form">
          <div className="form-stack">
            <Field label={t('rent.c_customer')} error={err(errors.customer)} required>
              <button type="button" className={`input rent-customer${customer ? '' : ' empty'}`} onClick={() => setPicking(true)}>
                <Icon name="users" size={16} />
                <span>{customer ? `${customer.name}${customer.phone ? ` · ${customer.phone}` : ''}` : t('rent.choose_customer')}</span>
              </button>
            </Field>
            <div className="form-grid">
              <Field label={t('rent.pickup')} required>
                <input className="input" type="datetime-local" value={starts} onChange={(e) => e.target.value && setStarts(e.target.value)} />
              </Field>
              <Field label={t('rent.return')} error={err(errors.times)} required hint={hours ? length(hours) : undefined}>
                <input className="input" type="datetime-local" value={ends} min={starts} onChange={(e) => e.target.value && setEnds(e.target.value)} />
              </Field>
            </div>
            <div className="rent-quick" role="group" aria-label={t('rent.quick_length')}>
              {[2, 4, 24, 72, 168].map((h) => (
                <Button key={h} size="sm" variant={hours === h ? 'secondary' : 'ghost'} onClick={() => setEnds(shift(starts, h))}>
                  {length(h)}
                </Button>
              ))}
            </div>

            <fieldset className="option-group">
              <legend>{t('rent.items')}</legend>
              <ul className="rent-lines">
                {lines.map((l) => {
                  const c = check?.lines[wanted.indexOf(l)];
                  return (
                    <li key={l.key}>
                      <select className="input" value={l.item} aria-label={t('rent.item')} onChange={(e) => setLine(l.key, { item: e.target.value, price: '' })}>
                        {active.map((i) => (
                          <option key={i.id} value={i.id}>
                            {i.name}
                          </option>
                        ))}
                      </select>
                      <input className="input rent-qty" inputMode="numeric" aria-label={t('rent.qty')} value={l.qty} onChange={(e) => setLine(l.key, { qty: Math.max(1, Math.min(1000, Number(e.target.value.replace(/\D/g, '')) || 1)) })} />
                      <input className="input rent-price" inputMode="decimal" aria-label={t('rent.unit_price')} value={l.price} placeholder={c?.price ? lek(c.price.price) : '—'} onChange={(e) => setLine(l.key, { price: e.target.value })} />
                      <span className="tabular rent-line-total">{c ? money(c.total) : ''}</span>
                      <button type="button" className="icon-btn" aria-label={t('rent.remove_item')} onClick={() => setLines((ls) => ls.filter((x) => x.key !== l.key))}>
                        <Icon name="x" size={16} />
                      </button>
                      {c ? (
                        <span className={`rent-line-note small ${c.problem ? 'text-danger' : 'muted'}`}>
                          {c.problem ?? [tn('rent.n_free', c.free), c.price ? priceParts(c.price) : null].filter(Boolean).join(' · ')}
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
              {err(errors.lines) ? (
                <p className="field-msg" role="alert">
                  <Icon name="alert" size={14} /> {errors.lines}
                </p>
              ) : null}
              <Button
                size="sm"
                icon="plus"
                variant="ghost"
                onClick={() => {
                  const next = active.find((i) => !lines.some((l) => l.item === i.id)) ?? active[0];
                  if (next) setLines((ls) => [...ls, { key: counter, item: next.id, qty: 1, price: '' }]);
                  setCounter((c) => c + 1);
                }}
              >
                {t('rent.add_item')}
              </Button>
              <p className="muted small">{t('rent.price_hint')}</p>
            </fieldset>
          </div>

          <div className="form-stack">
            <fieldset className="option-group">
              <legend>{t('rent.deposit')}</legend>
              <div className="segmented subtle" role="radiogroup" aria-label={t('rent.deposit')}>
                {(['cash', 'document', 'none'] as DepositKind[]).map((k) => (
                  <button key={k} role="radio" aria-checked={depositKind === k} className={depositKind === k ? 'active' : ''} onClick={() => setDepositKind(k)}>
                    {t(`rent.dep_${k}` as MessageKey)}
                  </button>
                ))}
              </div>
              {depositKind === 'cash' ? (
                <Field label={t('rent.deposit_amount')} hint={t('rent.deposit_cash_hint')} error={err(errors.deposit)}>
                  <input
                    className="input"
                    inputMode="decimal"
                    value={depositText}
                    onChange={(e) => {
                      setDepositTouched(true);
                      setDepositText(e.target.value);
                    }}
                  />
                </Field>
              ) : depositKind === 'document' ? (
                <Field label={t('rent.document_kept')} hint={t('rent.document_hint')} error={err(errors.note)}>
                  <input className="input" value={depositNote} maxLength={120} onChange={(e) => setDepositNote(e.target.value)} />
                </Field>
              ) : (
                <p className="muted small">{t('rent.dep_none_hint')}</p>
              )}
            </fieldset>
            {setup.locations.length > 1 ? (
              <Field label={t('rent.place')}>
                <select className="input" value={location ?? ''} onChange={(e) => setLocation(e.target.value || null)}>
                  <option value="">{t('rent.main_place')}</option>
                  {setup.locations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
            <Field label={t('rent.note')} counter={`${note.length}/1000`}>
              <textarea className="input" rows={2} value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
            </Field>
            <div className="rent-total-box">
              <span className="muted">{hours ? length(hours) : ''}</span>
              <strong className="tabular">{money(check?.total ?? 0)}</strong>
              {check?.problem ? <span className="text-danger small">{check.problem}</span> : check && !check.ok ? <span className="text-danger small">{t('rent.not_free_hint')}</span> : null}
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function priceParts(p: { weeks: number; days: number; hour_units: number }) {
  return [p.weeks ? tn('rent.n_weeks', p.weeks) : null, p.days ? tn('rent.n_days', p.days) : null, p.hour_units ? tn('rent.n_hours', p.hour_units) : null].filter(Boolean).join(' + ');
}

// ---- schedule ------------------------------------------------------------------------------

function addDays(d: string, n: number) {
  const x = new Date(`${d}T00:00:00Z`);
  x.setUTCDate(x.getUTCDate() + n);
  return x.toISOString().slice(0, 10);
}
const weekend = (d: string) => [0, 6].includes(new Date(`${d}T00:00:00Z`).getUTCDay());
const shortDay = (d: string) => new Intl.DateTimeFormat(undefined, { weekday: 'short', timeZone: 'UTC' }).format(new Date(`${d}T00:00:00Z`));

function ScheduleTab({ onOpen, onBook }: { onOpen: (id: string) => void; onBook?: (item: string, day: string) => void }) {
  const { fail } = useUi();
  const [from, setFrom] = useState<string | null>(null);
  const [days, setDays] = useState(14);
  const { data: setup } = useAction<Setup>('rental.setup');
  const start = from ?? (setup ? addDays(setup.today, -1) : null);
  const [cal, setCal] = useState<Schedule | null>(null);
  useEffect(() => {
    if (!start) return;
    action<Schedule>('rental.schedule', { from: start, days }).then(setCal, fail);
  }, [start, days, fail]);
  if (!cal || !start) return <Skeleton h={420} r="var(--radius-lg)" />;
  const n = cal.days.length;
  const a = new Date(cal.starts).getTime();
  const span = new Date(cal.ends).getTime() - a;
  const pos = (s: string, e: string) => {
    const l = Math.max(0, (new Date(s).getTime() - a) / span);
    const r = Math.min(1, (new Date(e).getTime() - a) / span);
    return { left: `${l * 100}%`, width: `${Math.max(0.6, (r - l) * 100)}%` };
  };
  // Rentals of an item share lanes, side by side where they do not overlap.
  const lanes = (list: Schedule['bars']) => {
    const out: Schedule['bars'][] = [];
    for (const b of [...list].sort((x, y) => x.starts.localeCompare(y.starts))) {
      const lane = out.find((l) => l.every((o) => o.ends <= b.starts || o.starts >= b.ends));
      if (lane) lane.push(b);
      else out.push([b]);
    }
    return out;
  };
  const grid = { gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` };
  return (
    <section className="panel htl-cal-panel">
      <div className="toolbar">
        <span className="row-actions">
          <Button size="sm" icon="back" onClick={() => setFrom(addDays(start, -7))}>
            {t('rent.earlier')}
          </Button>
          <Button size="sm" onClick={() => setFrom(addDays(cal.today, -1))}>
            {t('rent.today')}
          </Button>
          <Button size="sm" onClick={() => setFrom(addDays(start, 7))}>
            {t('rent.later')} <Icon name="chevronRight" size={14} />
          </Button>
          <input className="input htl-cal-date" type="date" value={start} onChange={(e) => e.target.value && setFrom(e.target.value)} aria-label={t('rent.from_day')} />
        </span>
        <div className="segmented" role="tablist" aria-label={t('rent.days_shown')}>
          {[14, 31].map((x) => (
            <button key={x} role="tab" aria-selected={days === x} className={days === x ? 'active' : ''} onClick={() => setDays(x)}>
              {tn('rent.n_days', x)}
            </button>
          ))}
        </div>
      </div>
      <div className="htl-cal-legend muted small">
        {(['quote', 'reserved', 'out', 'returned'] as State[]).map((s) => (
          <span key={s}>
            <i className={`htl-dot rent-bar-${s}`} /> {t(`rent.st_${s}` as MessageKey)}
          </span>
        ))}
        <span>
          <i className="htl-dot rent-bar-overdue" /> {t('rent.st_overdue')}
        </span>
        {onBook ? <span>{t('rent.cal_click_hint')}</span> : null}
      </div>
      {!cal.items.length ? (
        <EmptyState icon="box" title={t('rent.no_items_yet')} body={t('rent.no_items_hint')} />
      ) : (
        <div className="htl-cal" role="grid" aria-label={t('rent.tab_schedule')} style={{ '--cal-min': `${132 + n * (days > 14 ? 34 : 52)}px` } as CSSProperties}>
          <div className="htl-cal-row htl-cal-head" role="row">
            <span className="htl-cal-label" role="columnheader">
              {t('rent.item')}
            </span>
            <div className="htl-cal-cells" style={grid}>
              {cal.days.map((d) => (
                <span key={d} role="columnheader" className={`htl-cal-day${d === cal.today ? ' today' : ''}${weekend(d) ? ' weekend' : ''}`}>
                  <small>{shortDay(d)}</small>
                  <strong>{Number(d.slice(8))}</strong>
                </span>
              ))}
            </div>
          </div>
          {cal.items.map((it) => {
            const mine = lanes(cal.bars.filter((b) => b.item === it.id));
            return (
              <div key={it.id} className="htl-cal-group" role="rowgroup">
                <div className="htl-cal-row htl-cal-type" role="row">
                  <span className="htl-cal-label">
                    <strong>{it.name}</strong>
                    <span className="muted small">{t('rent.free_of', { n: it.units })}</span>
                  </span>
                  <div className="htl-cal-cells" style={grid}>
                    {it.free.map((f, i) =>
                      onBook && cal.days[i]! >= cal.today && f > 0 ? (
                        <button key={i} className="htl-cal-free rent-free-btn" onClick={() => onBook(it.id, cal.days[i]!)} aria-label={t('rent.book_on', { item: it.name, day: day(cal.days[i]!) })}>
                          {f}
                        </button>
                      ) : (
                        <span key={i} className={`htl-cal-free${f <= 0 ? ' full' : ''}`}>
                          {f}
                        </span>
                      ),
                    )}
                  </div>
                </div>
                {(mine.length ? mine : [[]]).map((lane, li) => (
                  <div key={li} className="htl-cal-row" role="row">
                    <span className="htl-cal-label muted small" />
                    <div className="htl-cal-cells rent-lane" style={grid}>
                      {cal.days.map((d, i) => (
                        <span key={d} className={`htl-cal-cell${weekend(d) ? ' weekend' : ''}`} style={{ gridColumn: `${i + 1}`, gridRow: 1 }} />
                      ))}
                      {lane.map((b) => (
                        <button
                          key={b.order}
                          className={`htl-bar rent-bar rent-bar-${b.overdue ? 'overdue' : b.state}`}
                          style={pos(b.starts, b.ends)}
                          onClick={() => onOpen(b.order)}
                          title={`${rentalName(b.number)} · ${b.customer_name} · ${dateTime(b.starts)} – ${dateTime(b.ends)}`}
                        >
                          <span className="htl-bar-name">
                            {b.qty > 1 ? `${b.qty} × ` : ''}
                            {b.customer_name}
                          </span>
                          <span className="htl-bar-sub">{rentalName(b.number)}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ---- items -----------------------------------------------------------------------------------

function ItemsTab() {
  const { can } = useSession();
  const { data, reload } = useAction<Item[]>('rental.items');
  const [edit, setEdit] = useState<Item | 'new' | null>(null);
  const manage = can('rental.manage');
  const priceText = (i: Item) =>
    [i.price_hour !== null ? t('rent.per_hour', { amount: money(i.price_hour) }) : null, i.price_day !== null ? t('rent.per_day', { amount: money(i.price_day) }) : null, i.price_week !== null ? t('rent.per_week', { amount: money(i.price_week) }) : null]
      .filter(Boolean)
      .join(' · ');
  return (
    <section className="panel">
      <div className="prot-history-head">
        <div>
          <h2 className="rep-sub">{t('rent.items_title')}</h2>
          <p className="muted small">{t('rent.items_hint')}</p>
        </div>
        {manage ? (
          <Button size="sm" icon="plus" onClick={() => setEdit('new')}>
            {t('rent.new_item')}
          </Button>
        ) : null}
      </div>
      {!data ? (
        <Skeleton h={240} />
      ) : data.length ? (
        <Table
          head={[t('rent.item'), t('rent.c_prices'), t('rent.c_units'), t('rent.c_deposit'), t('rent.c_30'), '']}
          phoneHide={[3, 4]}
          left={[1]}
          rows={data.map((i) => ({
            key: i.id,
            onClick: manage ? () => setEdit(i) : undefined,
            cells: [
              <span className="vat-party" key="n">
                <strong>{i.name}</strong>
                <span className="muted">{[i.category, i.active ? null : t('rent.inactive')].filter(Boolean).join(' · ')}</span>
              </span>,
              <span key="p" className="small">
                {priceText(i)}
              </span>,
              <span key="u" className="tabular">
                {t('rent.out_of', { out: i.out, total: i.units })}
              </span>,
              <span key="d" className="tabular">
                {i.deposit ? money(i.deposit) : '—'}
              </span>,
              <span key="s" className="vat-party">
                <span className="tabular">{money(i.value_30)}</span>
                <span className="muted">{tn('rent.n_rentals', i.rentals_30)}</span>
              </span>,
              manage ? <Icon key="e" name="chevronRight" size={16} /> : <span key="e" />,
            ],
          }))}
        />
      ) : (
        <EmptyState icon="box" title={t('rent.no_items_yet')} body={t('rent.no_items_hint')} action={manage ? <Button icon="plus" onClick={() => setEdit('new')}>{t('rent.new_item')}</Button> : undefined} />
      )}
      {edit ? (
        <ItemDialog
          item={edit === 'new' ? null : edit}
          onClose={() => setEdit(null)}
          onSaved={async () => {
            setEdit(null);
            await reload();
          }}
        />
      ) : null}
    </section>
  );
}

function ItemDialog({ item, onClose, onSaved }: { item: Item | null; onClose: () => void; onSaved: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [name, setName] = useState(item?.name ?? '');
  const [category, setCategory] = useState(item?.category ?? '');
  const [units, setUnits] = useState(String(item?.units ?? 1));
  const [hour, setHour] = useState(lek(item?.price_hour ?? null));
  const [dayP, setDayP] = useState(lek(item?.price_day ?? null));
  const [week, setWeek] = useState(lek(item?.price_week ?? null));
  const [deposit, setDeposit] = useState(lek(item?.deposit ?? 0));
  const [vat, setVat] = useState<number>(item?.vat ?? standardRateNow());
  const [minHours, setMinHours] = useState(String(item?.min_hours ?? 1));
  const [buffer, setBuffer] = useState(String(item?.buffer_minutes ?? 0));
  const [description, setDescription] = useState(item?.description ?? '');
  const [sequence, setSequence] = useState(String(item?.sequence ?? 0));
  const [active, setActive] = useState(item?.active ?? true);
  const [tried, setTried] = useState(false);
  const money_ = (s: string) => (s.trim() ? parseMoney(s) : null);
  const prices = { hour: money_(hour), day: money_(dayP), week: money_(week) };
  const bad = (s: string) => s.trim() !== '' && parseMoney(s) === null;
  const errors = {
    name: !name.trim() ? t('rent.e_name') : null,
    units: !(Number(units) >= 1) ? t('rent.e_units') : null,
    prices: prices.hour === null && prices.day === null && prices.week === null ? t('rent.e_prices') : null,
    amount: bad(hour) || bad(dayP) || bad(week) || bad(deposit) ? t('rent.e_amount') : null,
  };
  const save = () => {
    setTried(true);
    if (Object.values(errors).some(Boolean)) return;
    void run(async () => {
      await action('rental.save_item', {
        id: item?.id ?? null,
        name: name.trim(),
        category: category.trim() || null,
        units: Number(units),
        price_hour: prices.hour,
        price_day: prices.day,
        price_week: prices.week,
        deposit: money_(deposit) ?? 0,
        vat,
        min_hours: Math.max(1, Number(minHours) || 1),
        buffer_minutes: Number(buffer) || 0,
        description: description.trim() || null,
        sequence: Number(sequence) || 0,
        active,
      });
      await onSaved();
    }, t('rent.item_saved'));
  };
  const err = (e: string | null) => (tried ? e : null);
  const digits = (set: (v: string) => void) => (e: { target: { value: string } }) => set(e.target.value.replace(/\D/g, ''));
  return (
    <Dialog
      title={item ? item.name : t('rent.new_item')}
      onClose={onClose}
      footer={
        <Button variant="primary" icon="check" busy={busy} onClick={save}>
          {t('common.save')}
        </Button>
      }
    >
      <div className="form-stack">
        <div className="form-grid">
          <Field label={t('rent.item_name')} error={err(errors.name)} required>
            <input className="input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label={t('rent.group')} hint={t('rent.group_hint')}>
            <input className="input" value={category} maxLength={40} onChange={(e) => setCategory(e.target.value)} />
          </Field>
          <Field label={t('rent.units')} hint={t('rent.units_hint')} error={err(errors.units)} required>
            <input className="input" inputMode="numeric" value={units} onChange={digits(setUnits)} />
          </Field>
          <Field label={t('rent.deposit_per_unit')} hint={t('rent.deposit_per_unit_hint')}>
            <input className="input" inputMode="decimal" value={deposit} placeholder="0" onChange={(e) => setDeposit(e.target.value)} />
          </Field>
        </div>
        <fieldset className="option-group">
          <legend>{t('rent.prices')}</legend>
          <div className="form-grid rent-prices">
            <Field label={t('rent.price_hour')}>
              <input className="input" inputMode="decimal" value={hour} placeholder="—" onChange={(e) => setHour(e.target.value)} />
            </Field>
            <Field label={t('rent.price_day')}>
              <input className="input" inputMode="decimal" value={dayP} placeholder="—" onChange={(e) => setDayP(e.target.value)} />
            </Field>
            <Field label={t('rent.price_week')}>
              <input className="input" inputMode="decimal" value={week} placeholder="—" onChange={(e) => setWeek(e.target.value)} />
            </Field>
          </div>
          {err(errors.prices) || err(errors.amount) ? (
            <p className="field-msg" role="alert">
              <Icon name="alert" size={14} /> {errors.prices ?? errors.amount}
            </p>
          ) : (
            <p className="muted small">{t('rent.prices_hint')}</p>
          )}
        </fieldset>
        <div className="form-grid">
          <Field label={t('rent.vat')} hint={t('rent.vat_hint')}>
            <TaxRateSelect value={vat} onChange={setVat} />
          </Field>
          <Field label={t('rent.min_hours')} hint={t('rent.min_hours_hint')}>
            <input className="input" inputMode="numeric" value={minHours} onChange={digits(setMinHours)} />
          </Field>
          <Field label={t('rent.buffer')} hint={t('rent.buffer_hint')}>
            <input className="input" inputMode="numeric" value={buffer} onChange={digits(setBuffer)} />
          </Field>
          <Field label={t('rent.sequence')} hint={t('rent.sequence_hint')}>
            <input className="input" inputMode="numeric" value={sequence} onChange={digits(setSequence)} />
          </Field>
        </div>
        <Field label={t('rent.description')} counter={`${description.length}/1000`}>
          <textarea className="input" rows={2} value={description} maxLength={1000} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="setting-row">
          <div>
            <strong>{t('rent.active')}</strong>
            <p className="muted">{t('rent.active_hint')}</p>
          </div>
          <Switch checked={active} onChange={setActive} label={t('rent.active')} />
        </div>
      </div>
    </Dialog>
  );
}

// ---- results -------------------------------------------------------------------------------

function ReportsTab({ today }: { today: string }) {
  const [from, setFrom] = useState(`${today.slice(0, 8)}01`);
  const [to, setTo] = useState(today);
  const { data } = useAction<Report>('rental.report', { from, to });
  const presets: { key: string; label: string; from: string; to: string }[] = [
    { key: 'month', label: t('rent.r_this_month'), from: `${today.slice(0, 8)}01`, to: today },
    { key: '30', label: t('rent.r_30_days'), from: addDays(today, -29), to: today },
    { key: '90', label: t('rent.r_90_days'), from: addDays(today, -89), to: today },
  ];
  const csv = () => {
    if (!data) return;
    downloadCsv(
      `rentals-${from}-${to}.csv`,
      [t('rent.item'), t('rent.c_rentals'), t('rent.c_units_rented'), t('rent.c_value'), t('rent.c_use')],
      data.items.map((i) => [i.name, i.rentals, i.qty, (i.value / 100).toFixed(2), i.use_pct]),
    );
  };
  return (
    <div className="form-stack">
      <section className="panel">
        <div className="toolbar">
          <div className="segmented subtle" role="radiogroup" aria-label={t('rent.r_period')}>
            {presets.map((p) => (
              <button key={p.key} role="radio" aria-checked={from === p.from && to === p.to} className={from === p.from && to === p.to ? 'active' : ''} onClick={() => (setFrom(p.from), setTo(p.to))}>
                {p.label}
              </button>
            ))}
          </div>
          <div className="shop-range">
            <input className="input" type="date" value={from} max={to} aria-label={t('rent.r_from')} onChange={(e) => e.target.value && setFrom(e.target.value)} />
            <span className="muted">–</span>
            <input className="input" type="date" value={to} min={from} aria-label={t('rent.r_to')} onChange={(e) => e.target.value && setTo(e.target.value)} />
          </div>
        </div>
        {!data ? (
          <Skeleton h={110} />
        ) : (
          <section className="rep-kpis" aria-label={t('rent.r_summary')}>
            <Kpi label={t('rent.r_rent')} value={money(data.rent)} hint={tn('rent.n_rentals', data.rentals)} />
            <Kpi label={t('rent.r_extra')} value={money(data.time + data.damage)} hint={t('rent.r_extra_hint', { time: money(data.time), damage: money(data.damage) })} />
            <Kpi label={t('rent.r_length')} value={data.avg_hours ? length(Math.round(data.avg_hours)) : '—'} hint={t('rent.r_length_hint')} />
            <Kpi label={t('rent.r_late')} value={String(data.late)} hint={data.rentals ? t('rent.r_late_rate', { pct: Math.round((data.late / data.rentals) * 100) }) : undefined} warn={data.rentals > 0 && data.late / data.rentals > 0.15} />
          </section>
        )}
      </section>
      {data ? (
        <div className="shop-overview">
          <section className="panel">
            <div className="prot-history-head">
              <h2 className="rep-sub">{t('rent.r_by_item')}</h2>
              <Button size="sm" icon="download" disabled={!data.items.length} onClick={csv}>
                {t('rent.csv')}
              </Button>
            </div>
            <Table
              head={[t('rent.item'), t('rent.c_rentals'), t('rent.c_value'), t('rent.c_use')]}
              phoneHide={[1]}
              empty={t('rent.r_none')}
              rows={data.items.map((i) => ({
                key: i.item,
                cells: [
                  i.name,
                  <span key="r" className="tabular">
                    {i.rentals}
                  </span>,
                  <span key="v" className="tabular">
                    {money(i.value)}
                  </span>,
                  <span key="u" className="rent-use-cell">
                    <span className="rent-meter small">
                      <span style={{ width: `${i.use_pct}%` }} />
                    </span>
                    <span className="tabular">{i.use_pct}%</span>
                  </span>,
                ],
              }))}
            />
            <p className="muted small">{t('rent.r_use_hint')}</p>
          </section>
          <div className="shop-side">
            <section className="panel">
              <h2 className="rep-sub">{t('rent.r_reasons')}</h2>
              {data.cancels.length ? (
                <ul className="shop-ways">
                  {data.cancels.map((r) => (
                    <li key={r.reason ?? '-'}>
                      <span>{r.reason ?? '—'}</span>
                      <strong className="tabular">{r.n}</strong>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted">{t('rent.r_no_cancel')}</p>
              )}
            </section>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---- settings ------------------------------------------------------------------------------

function SettingsTab() {
  const { data, reload } = useAction<Setup>('rental.setup');
  if (!data) return <Skeleton h={320} r="var(--radius-lg)" />;
  return <RentalSettings setup={data} onSaved={() => void reload()} />;
}

function RentalSettings({ setup, onSaved }: { setup: Setup; onSaved: () => void }) {
  const { busy, run } = useSave();
  const [grace, setGrace] = useState(String(setup.settings.grace_minutes));
  const [vat, setVat] = useState<number>(Number(setup.settings.extras_vat));
  const [terms, setTerms] = useState(setup.settings.terms ?? '');
  const save = () =>
    void run(async () => {
      await action('rental.save_settings', { grace_minutes: Number(grace) || 0, extras_vat: vat, terms: terms.trim() || null });
      onSaved();
    }, t('rent.saved'));
  const tip = useMemo(() => t('rent.s_grace_example', { n: Number(grace) || 0 }), [grace]);
  return (
    <div className="online-settings">
      <section className="card">
        <header className="card-head">
          <div>
            <h2>
              <Icon name="clock" size={20} /> {t('rent.s_returns')}
            </h2>
            <span className="muted">{t('rent.s_returns_hint')}</span>
          </div>
        </header>
        <div className="form-grid">
          <Field label={t('rent.s_grace')} hint={tip}>
            <input className="input" inputMode="numeric" value={grace} onChange={(e) => setGrace(e.target.value.replace(/\D/g, '').slice(0, 4))} />
          </Field>
          <Field label={t('rent.s_extras_vat')} hint={t('rent.s_extras_vat_hint')}>
            <TaxRateSelect value={vat} onChange={setVat} />
          </Field>
        </div>
      </section>
      <section className="card">
        <header className="card-head">
          <div>
            <h2>
              <Icon name="book" size={20} /> {t('rent.s_terms')}
            </h2>
            <span className="muted">{t('rent.s_terms_hint')}</span>
          </div>
        </header>
        <Field label={t('rent.s_terms_text')} counter={`${terms.length}/10000`}>
          <textarea className="input" rows={6} value={terms} maxLength={10000} onChange={(e) => setTerms(e.target.value)} />
        </Field>
      </section>
      <div className="form-actions">
        <Button variant="primary" icon="check" busy={busy} onClick={save}>
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}
